export type VisitRecord = {
  path: string;
  title: string;
  count: number;
  lastVisitedAt: string;
};

const STORAGE_KEY = 'amfs.portal.home.visits';
const MAX_VISITS = 25;

export const VISIT_HISTORY_UPDATED_EVENT = 'amfs-portal:visit-history-updated';

const resolveVisitTitle = (path: string) => {
  if (path === '/') {
    return 'home';
  }

  if (path === '/catalog') {
    return 'application';
  }

  if (path.startsWith('/catalog/')) {
    return 'catalog';
  }

  if (path === '/api-docs') {
    return 'apis';
  }

  if (path === '/search') {
    return 'search';
  }

  if (path === '/settings') {
    return 'settings';
  }

  if (path === '/create') {
    return 'create';
  }

  if (path === '/guide') {
    return 'guide';
  }

  if (path === '/catalog-graph') {
    return 'catalog graph';
  }

  if (path === '/notifications') {
    return 'notifications';
  }

  return undefined;
};

export const readVisitHistory = (): VisitRecord[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  const raw = window.localStorage.getItem(STORAGE_KEY);

  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as VisitRecord[];

    return Array.isArray(parsed)
      ? parsed.filter(
          visit =>
            typeof visit?.path === 'string' &&
            typeof visit?.title === 'string' &&
            typeof visit?.count === 'number' &&
            typeof visit?.lastVisitedAt === 'string',
        )
      : [];
  } catch {
    return [];
  }
};

const writeVisitHistory = (visits: VisitRecord[]) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(visits));
  window.dispatchEvent(new Event(VISIT_HISTORY_UPDATED_EVENT));
};

export const recordVisit = (pathname: string) => {
  const normalizedPath = pathname === '/home' ? '/' : pathname;
  const title = resolveVisitTitle(normalizedPath);

  if (!title || typeof window === 'undefined') {
    return;
  }

  const visits = readVisitHistory();
  const now = new Date().toISOString();
  const existingVisit = visits.find(visit => visit.path === normalizedPath);

  if (existingVisit) {
    const nextVisits = visits.map(visit =>
      visit.path === normalizedPath
        ? {
            ...visit,
            count: visit.count + 1,
            lastVisitedAt: now,
          }
        : visit,
    );

    writeVisitHistory(nextVisits);
    return;
  }

  writeVisitHistory(
    [
      {
        path: normalizedPath,
        title,
        count: 1,
        lastVisitedAt: now,
      },
      ...visits,
    ].slice(0, MAX_VISITS),
  );
};

export const getTopVisited = (visits: VisitRecord[], limit = 6) =>
  [...visits].sort((left, right) => right.count - left.count).slice(0, limit);

export const getRecentlyVisited = (visits: VisitRecord[], limit = 6) =>
  [...visits]
    .sort(
      (left, right) =>
        new Date(right.lastVisitedAt).getTime() -
        new Date(left.lastVisitedAt).getTime(),
    )
    .slice(0, limit);

export const formatRelativeTime = (value: string) => {
  const deltaSeconds = Math.max(
    0,
    Math.round((Date.now() - new Date(value).getTime()) / 1000),
  );

  if (deltaSeconds < 60) {
    return `${deltaSeconds || 1} second${deltaSeconds === 1 ? '' : 's'} ago`;
  }

  const deltaMinutes = Math.round(deltaSeconds / 60);

  if (deltaMinutes < 60) {
    return `${deltaMinutes} minute${deltaMinutes === 1 ? '' : 's'} ago`;
  }

  const deltaHours = Math.round(deltaMinutes / 60);

  if (deltaHours < 24) {
    return `${deltaHours} hour${deltaHours === 1 ? '' : 's'} ago`;
  }

  const deltaDays = Math.round(deltaHours / 24);
  return `${deltaDays} day${deltaDays === 1 ? '' : 's'} ago`;
};